import { defineSseEvent } from '@terra/shared-sse-contracts';
import {
  CredentialDisableReasonSchema,
  CredentialLoginHealthStatusSchema,
  TargetTypeSchema,
} from '@terra/shared-schemas-orchestrator';
import {
  AuthMaterialExpirySchema,
  CachedCredentialArtifactSummarySchema,
} from '@terra/shared-contracts-authenticator';
import { z } from 'zod';

export const CREDENTIALS_VERIFICATION_UPDATED_SSE_TYPE =
  'CREDENTIALS_VERIFICATION_UPDATED' as const;
export const CREDENTIAL_STATUS_CHANGED_SSE_TYPE =
  'CREDENTIAL_STATUS_CHANGED' as const;
export const CREDENTIALS_LOGIN_HEALTH_UPDATED_SSE_TYPE =
  'CREDENTIALS_LOGIN_HEALTH_UPDATED' as const;

export const CredentialLoginHealthSnapshotSchema = z.object({
  status: CredentialLoginHealthStatusSchema,
  reason: z.string().nullable(),
  field: z.string().nullable(),
  declared: z.string().nullable(),
  observed: z.string().nullable(),
  evidenceSource: z.string().nullable(),
  observedAt: z.iso.datetime(),
});

export const CredentialsVerificationSsePayloadSchema = z.object({
  credentialId: z.string(),
  applicationId: z.string(),
  targetType: TargetTypeSchema,
  verification: z.object({
    status: z.enum(['PENDING', 'VERIFYING', 'VERIFIED', 'FAILED']),
    lastError: z.string().nullable(),
    lastAttemptAt: z.iso.datetime().nullable(),
    verifiedAt: z.iso.datetime().nullable(),
    authMaterialExpiry: AuthMaterialExpirySchema.nullable().optional(),
    artifacts: z.array(CachedCredentialArtifactSummarySchema).optional(),
  }),
});

export const CredentialStatusChangedSsePayloadSchema = z.object({
  credentialId: z.string(),
  applicationId: z.string(),
  targetType: TargetTypeSchema,
  isDisabled: z.boolean(),
  disableReason: CredentialDisableReasonSchema.nullable(),
  username: z.string(),
});

export const CredentialsLoginHealthSsePayloadSchema = z.object({
  credentialId: z.string(),
  applicationId: z.string(),
  targetType: TargetTypeSchema,
  loginHealth: CredentialLoginHealthSnapshotSchema,
});

export const CREDENTIALS_VERIFICATION_UPDATED_SSE_EVENT = defineSseEvent({
  type: CREDENTIALS_VERIFICATION_UPDATED_SSE_TYPE,
  payload: CredentialsVerificationSsePayloadSchema,
  channelPrefix: 'credentials',
  channelKey: ({ applicationId }) => applicationId,
  target: {
    type: ({ targetType }) => targetType,
    id: ({ applicationId }) => applicationId,
  },
});

export const CREDENTIAL_STATUS_CHANGED_SSE_EVENT = defineSseEvent({
  type: CREDENTIAL_STATUS_CHANGED_SSE_TYPE,
  payload: CredentialStatusChangedSsePayloadSchema,
  channelPrefix: 'credentials',
  channelKey: ({ applicationId }) => applicationId,
  target: {
    type: ({ targetType }) => targetType,
    id: ({ applicationId }) => applicationId,
  },
});

export const CREDENTIALS_LOGIN_HEALTH_UPDATED_SSE_EVENT = defineSseEvent({
  type: CREDENTIALS_LOGIN_HEALTH_UPDATED_SSE_TYPE,
  payload: CredentialsLoginHealthSsePayloadSchema,
  channelPrefix: 'credentials',
  channelKey: ({ applicationId }) => applicationId,
  target: {
    type: ({ targetType }) => targetType,
    id: ({ applicationId }) => applicationId,
  },
});

export type CredentialLoginHealthSnapshot = z.infer<
  typeof CredentialLoginHealthSnapshotSchema
>;
export type CredentialsLoginHealthSsePayload = z.infer<
  typeof CredentialsLoginHealthSsePayloadSchema
>;
export type CredentialsVerificationSsePayload = z.infer<
  typeof CredentialsVerificationSsePayloadSchema
>;
export type CredentialStatusChangedSsePayload = z.infer<
  typeof CredentialStatusChangedSsePayloadSchema
>;
